import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Map, CalendarPlus, Users, UserCheck, FileCheck, ClipboardList, MessageSquare, ArrowRight } from 'lucide-react';
export function UseCasesHubPage() {
  const useCases = [{
    title: 'Plan a Regional Tour',
    desc: 'Turn a vague route idea into a booking campaign with venue targeting and outreach.',
    icon: Map,
    path: '/use-cases/plan-a-regional-tour',
    audiences: ['Artists', 'Managers']
  }, {
    title: 'Fill an Open Date Fast',
    desc: 'Create an event need, discover artists that fit, and move quickly to offer.',
    icon: CalendarPlus,
    path: '/use-cases/fill-an-open-date-fast',
    audiences: ['Venues', 'Promoters']
  }, {
    title: 'Run a Roster Without Chaos',
    desc: 'Track campaigns, opportunities, contracts, and payments across multiple artists.',
    icon: Users,
    path: '/use-cases/run-a-roster-without-chaos',
    audiences: ['Agents', 'Managers']
  }, {
    title: 'Lock the Lineup Before You Lock the Show',
    desc: 'Check member availability, track conflicts, and request confirmations.',
    icon: UserCheck,
    path: '/use-cases/lock-the-lineup-before-you-lock-the-show',
    audiences: ['Bands', 'Managers']
  }, {
    title: 'Move from Offer to Deposit to Confirmed Show',
    desc: 'Track offers, approvals, contracts, signatures, and deposits in one place.',
    icon: FileCheck,
    path: '/use-cases/offer-to-deposit-to-confirmed-show',
    audiences: ['All']
  }, {
    title: "Manage the Show After It's Booked",
    desc: 'Keep logistics, contacts, itinerary, and finance tied to the confirmed show.',
    icon: ClipboardList,
    path: '/use-cases/manage-the-show-after-its-booked',
    audiences: ['All']
  }, {
    title: 'Operate Booker in Plain Language',
    desc: 'Use clicks for precision, and plain language when you want speed.',
    icon: MessageSquare,
    path: '/use-cases/operate-booker-in-plain-language',
    audiences: ['All']
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7414">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 mb-20 text-center" data-id="element-7415">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-6xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7416">
          Real booking workflows,{' '}
          <span className="text-primary" data-id="element-7417">mapped end to end.</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg md:text-xl text-textMuted max-w-3xl mx-auto leading-relaxed" data-id="element-7418">
          Whether you are trying to plan a regional tour, fill an open date, run
          a roster, lock a lineup, or move from offer to deposit, Booker gives
          you a cleaner path through the work.
        </motion.p>
      </section>

      {/* Use Case Grid */}
      <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-7419">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7420">
          {useCases.map((uc, i) => <motion.div key={uc.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-surface border border-border rounded-2xl p-6 hover:border-primary/50 transition-colors group flex flex-col" data-id="element-7421">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-7422">
                <uc.icon className="w-6 h-6 text-primary" data-id="element-7423" />
              </div>
              <h3 className="text-xl font-bold text-textMain mb-3" data-id="element-7424">
                {uc.title}
              </h3>
              <p className="text-textMuted text-sm mb-6 flex-1" data-id="element-7425">{uc.desc}</p>

              <div className="flex flex-wrap gap-2 mb-6" data-id="element-7426">
                {uc.audiences.map(aud => <span key={aud} className="px-2.5 py-1 rounded-full bg-background border border-border text-xs font-medium text-textMuted" data-id="element-7427">
                    {aud}
                  </span>)}
              </div>

              <Link to={uc.path} className="inline-flex items-center text-sm font-medium text-primary hover:text-primaryHover transition-colors" data-id="element-7428">
                View use case{' '}
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-7429" />
              </Link>
            </motion.div>)}
        </div>
      </section>

      {/* Role Strip */}
      <section className="max-w-7xl mx-auto px-6 mb-24" data-id="element-7430">
        <div className="bg-surface border border-border rounded-2xl p-8 text-center" data-id="element-7431">
          <h2 className="text-xl font-bold text-textMain mb-6" data-id="element-7432">
            Who this is for
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4" data-id="element-7433">
            <Link to="/for-artists" className="w-full md:w-auto px-6 py-3 bg-background border border-border rounded-xl text-textMain font-medium hover:border-primary/50 transition-colors flex items-center justify-center" data-id="element-7434">
              Artists & Bands{' '}
              <ArrowRight className="w-4 h-4 ml-2 text-textMuted" data-id="element-7435" />
            </Link>
            <Link to="/for-venues" className="w-full md:w-auto px-6 py-3 bg-background border border-border rounded-xl text-textMain font-medium hover:border-primary/50 transition-colors flex items-center justify-center" data-id="element-7436">
              Venues & Promoters{' '}
              <ArrowRight className="w-4 h-4 ml-2 text-textMuted" data-id="element-7437" />
            </Link>
            <Link to="/for-agents" className="w-full md:w-auto px-6 py-3 bg-background border border-border rounded-xl text-textMain font-medium hover:border-primary/50 transition-colors flex items-center justify-center" data-id="element-7438">
              Agents & Managers{' '}
              <ArrowRight className="w-4 h-4 ml-2 text-textMuted" data-id="element-7439" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7440">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-b from-surface to-background border border-border rounded-3xl p-12 relative overflow-hidden" data-id="element-7441">
          <div className="absolute inset-0 bg-primary/5" data-id="element-7442"></div>
          <div className="relative z-10" data-id="element-7443">
            <h2 className="text-3xl font-bold text-textMain mb-8" data-id="element-7444">
              Explore your workflow
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7445">
              <Link to="/onboarding" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7446">
                Start free
              </Link>
              <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7447">
                Book a demo
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>;
}